import React, { useCallback, useState } from 'react';
import { TaskDropdown, TaskDrop, TaskFilterDiv, Data, GroupDiv, DropText, Switch, Status } from '../accounting/styles';
import { Options, SameIcon, DIV, SVGDiv, TaskListName } from '../accounting/styles';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import { Dropdown, DropdownItem } from '../Dropdown';
import {
  TASK_LIST_TYPE,
  SHOW_COMPLETED_TASK_BY_DATE,
  SHOW_COMPLETED_TASK_FILTER_NAME,
  SHOW_TASK_GROUPING_TYPE,
  SHOW_TASK_GROUPING_NAME,
  SHOW_TASK_BY_ORDER,
  SHOW_TASK_ORDER_NAME
} from '../../global/constants';
import TaskFilterCustomDropdown from '../taskFilterDropdown/taskFilterDropdown';
import { DisplayBtn } from '../viewCard/style';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { updateWorkspaceViewPreference } from '../../helpers/firebaseHelper';
import { defaultDisplayFilters } from '../../mobx/common';

interface DisplayFilterDropdownProps {
  className?: string;
}

const DisplayFilterDropdown: React.FC<DisplayFilterDropdownProps> = () => {
  const [isOrderDropdownOpen, setIsOrderDropdownOpen] = useState(false);
  const [isOpenGroupMenu, setIsOpenGroupMenu] = useState(false);
  const [isOpenCompletedTaskDropdown, setIsOpenCompletedTaskDropdown] = useState(false);

  //Mobx store variables
  const { viewStore, taskStore } = rootStore;
  const { currentView, createViewInput } = viewStore;
  const { isTaskFilterOpen } = taskStore;

  //Other variables
  const displayFilters = createViewInput?.id
    ? createViewInput?.displayFilters
    : currentView?.displayFilters || defaultDisplayFilters;

  const groupMenuItems = [
    { key: SHOW_TASK_GROUPING_TYPE.NO_GROUPING, label: 'No Grouping' },
    { key: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_PRIORITY, label: 'Priority' },
    { key: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_USER, label: 'User' },
    { key: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS, label: 'Status' }
  ];

  const handleTaskListTypeChange = useCallback(
    async (type: number) => {
      if (createViewInput?.id) {
        createViewInput.updateTaskFilterType('taskViewType', type);
      } else {
        if (currentView) {
          currentView.updateTaskFilterType('taskViewType', type);
          await updateWorkspaceViewPreference(currentView?.id, { taskViewType: type });
        }
      }
    },
    [createViewInput, currentView]
  );

  const handleShowRecentlyCompletedTaskChange = useCallback(
    async (value: boolean) => {
      if (createViewInput?.id) {
        createViewInput.updateTaskFilterType('showRecentlyCompletedTask', value);
      } else {
        if (currentView) {
          currentView.updateTaskFilterType('showRecentlyCompletedTask', value);
          await updateWorkspaceViewPreference(currentView?.id, { showRecentlyCompletedTask: value });
        }
      }
    },
    [createViewInput, currentView]
  );

  const handleShowCompletedTaskByFilterChange = useCallback(
    async (value: number) => {
      if (createViewInput?.id) {
        createViewInput.updateTaskFilterType('showCompletedTaskByFilter', value);
      } else {
        if (currentView) {
          currentView.updateTaskFilterType('showCompletedTaskByFilter', value);
          await updateWorkspaceViewPreference(currentView?.id, { showCompletedTaskByFilter: value });
        }
      }
      setIsOpenCompletedTaskDropdown(false);
    },
    [createViewInput, currentView]
  );

  const handleGroupingChange = useCallback(
    async (value: number) => {
      if (createViewInput?.id) {
        createViewInput.updateTaskFilterType('groupingStatus', value);
      } else {
        if (currentView) {
          currentView.updateTaskFilterType('groupingStatus', value);
          await updateWorkspaceViewPreference(currentView?.id, { groupingStatus: value });
        }
      }
      setIsOpenGroupMenu(false);
    },
    [createViewInput, currentView]
  );

  const handleOrderingChange = useCallback(
    async (value: number) => {
      if (createViewInput?.id) {
        createViewInput.updateTaskFilterType('orderByStatus', value);
      } else {
        if (currentView) {
          currentView.updateTaskFilterType('orderByStatus', value);
          await updateWorkspaceViewPreference(currentView?.id, { orderByStatus: value });
        }
      }
      setIsOrderDropdownOpen(false);
    },
    [createViewInput, currentView]
  );

  const handleShowSubTaskChange = useCallback(
    async (value: boolean) => {
      if (createViewInput?.id) {
        createViewInput.updateTaskFilterType('showSubTask', value);
      } else {
        if (currentView) {
          currentView.updateTaskFilterType('showSubTask', value);
          await updateWorkspaceViewPreference(currentView?.id, { showSubTask: value });
        }
      }
    },
    [createViewInput, currentView]
  );

  const handleShowEmptyGroupChange = useCallback(
    async (value: boolean) => {
      if (createViewInput?.id) {
        createViewInput.updateTaskFilterType('showEmptyGroup', value);
      } else {
        if (currentView) {
          currentView.updateTaskFilterType('showEmptyGroup', value);
          await updateWorkspaceViewPreference(currentView?.id, { showEmptyGroup: value });
        }
      }
    },
    [createViewInput, currentView]
  );

  const onGroupButtonClick = useCallback(() => {
    setIsOpenGroupMenu(!isOpenGroupMenu);
  }, [isOpenGroupMenu]);

  const onButtonClickOrdering = useCallback(() => {
    setIsOrderDropdownOpen(!isOrderDropdownOpen);
  }, [isOrderDropdownOpen]);

  const onTaskButtonClick = useCallback(() => {
    setIsOpenCompletedTaskDropdown(!isOpenCompletedTaskDropdown);
  }, [isOpenCompletedTaskDropdown]);

  const renderTaskFilterDropdown = () => (
    <TaskDropdown>
      <TaskDrop isTaskList>
        <Data>
          <Options>
            <SameIcon
              isCardActive={displayFilters.taskViewType === TASK_LIST_TYPE.TASKS}
              onClick={() => handleTaskListTypeChange(TASK_LIST_TYPE.TASKS)}
              className='display-filterDropdown'>
              <DIV>
                <SVGDiv>
                  <SVGIcon
                    name='three-parallel-line-icon'
                    width='14'
                    height='14'
                    viewBox='0 0 14 14'
                    className='line-fill-color'
                  />
                </SVGDiv>
                <TaskListName isCardActive={displayFilters.taskViewType === TASK_LIST_TYPE.TASKS}>List</TaskListName>
              </DIV>
            </SameIcon>
            <SameIcon
              isCardActive={displayFilters.taskViewType === TASK_LIST_TYPE.TASK_GRID}
              onClick={() => handleTaskListTypeChange(TASK_LIST_TYPE.TASK_GRID)}
              className='display-filterDropdown'>
              <DIV>
                <SVGDiv>
                  <SVGIcon name='layout-icon' width='14' height='14' viewBox='0 0 14 14' className='fill-color' />
                </SVGDiv>
                <TaskListName isCardActive={displayFilters.taskViewType === TASK_LIST_TYPE.TASK_GRID}>
                  Grid
                </TaskListName>
              </DIV>
            </SameIcon>
            {/* <SameIcon
              isCardActive={displayFilters.taskViewType === TASK_LIST_TYPE.CALANDAR_VIEW}
              onClick={() => handleTaskListTypeChange(TASK_LIST_TYPE.CALANDAR_VIEW)}>
              <DIV>
                <SVGDiv>
                  <SVGIcon
                    name='calendar-icon'
                    width='14'
                    height='14'
                    viewBox='0 0 18 18'
                    className='calendar-fill-color'
                  />
                </SVGDiv>
                <TaskListName isCardActive={displayFilters.taskViewType === TASK_LIST_TYPE.CALANDAR_VIEW}>
                  Calendar
                </TaskListName>
              </DIV>
            </SameIcon> */}
          </Options>
        </Data>
        <TaskFilterDiv>
          <Data>
            <GroupDiv>
              <SVGDiv>
                <SVGIcon name='group-icon' width='14' height='14' viewBox='0 0 16 16' fill='none' />
              </SVGDiv>
              <DropText>Grouping</DropText>
            </GroupDiv>
            <Dropdown
              modalTitle='Grouping'
              content={
                <Status onClick={onGroupButtonClick}>
                  <p>{SHOW_TASK_GROUPING_NAME[displayFilters.groupingStatus]}</p>
                  <SVGIcon
                    name='dropdown-icon'
                    width='20'
                    height='20'
                    viewBox='0 0 24 24'
                    fill='none'
                    className={isOpenGroupMenu ? 'dropdown-icon fill-color' : 'fill-color'}
                  />
                </Status>
              }
              trigger='click'>
              {groupMenuItems.map(({ key, label }) => (
                <DropdownItem
                  key={key}
                  label={label}
                  onClick={() => handleGroupingChange(Number(key))}
                  isSelected={displayFilters.groupingStatus === Number(key)}
                />
              ))}
            </Dropdown>
          </Data>
          <Data>
            <GroupDiv>
              <SVGDiv>
                <SVGIcon
                  name='small-filters-icon'
                  width='14'
                  height='14'
                  viewBox='0 0 14 14'
                  className='filters-icon-color'
                />
              </SVGDiv>
              <DropText>Ordering</DropText>
            </GroupDiv>
            <Dropdown
              modalTitle='Ordering'
              content={
                <Status onClick={onButtonClickOrdering}>
                  <p>{SHOW_TASK_ORDER_NAME[displayFilters.orderByStatus || SHOW_TASK_BY_ORDER.CUSTOM]}</p>
                  <SVGIcon
                    name='dropdown-icon'
                    width='20'
                    height='20'
                    viewBox='0 0 24 24'
                    fill='none'
                    className={isOrderDropdownOpen ? 'dropdown-icon fill-color' : 'fill-color'}
                  />
                </Status>
              }
              trigger='click'>
              {Object.entries(SHOW_TASK_ORDER_NAME).map(([key, label]) => (
                <DropdownItem
                  key={key}
                  label={label}
                  onClick={() => handleOrderingChange(Number(key))}
                  isSelected={displayFilters.orderByStatus === Number(key)}
                />
              ))}
            </Dropdown>
          </Data>
        </TaskFilterDiv>
      </TaskDrop>
      <TaskFilterDiv>
        {displayFilters.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS && (
          <TaskDrop isTaskList>
            <Data>
              <GroupDiv>
                <DropText>Task completed by recency</DropText>
              </GroupDiv>
              <Switch>
                <label className='switch'>
                  <input
                    type='checkbox'
                    checked={displayFilters.showRecentlyCompletedTask}
                    onChange={(e) => handleShowRecentlyCompletedTaskChange(e.target.checked)}
                  />
                  <span className='slider round'></span>
                </label>
              </Switch>
            </Data>
            {displayFilters.taskViewType !== TASK_LIST_TYPE.CALANDAR_VIEW && (
              <Data>
                <GroupDiv>
                  <DropText>Completed tasks</DropText>
                </GroupDiv>
                <Dropdown
                  modalTitle='Completed tasks'
                  content={
                    <Status onClick={onTaskButtonClick}>
                      <p>
                        {
                          SHOW_COMPLETED_TASK_FILTER_NAME[
                            displayFilters.showCompletedTaskByFilter || SHOW_COMPLETED_TASK_BY_DATE.LAST_MONTH
                          ]
                        }
                      </p>
                      <SVGIcon
                        name='dropdown-icon'
                        width='20'
                        height='20'
                        viewBox='0 0 24 24'
                        fill='none'
                        className={isOpenCompletedTaskDropdown ? 'dropdown-icon fill-color' : 'fill-color'}
                      />
                    </Status>
                  }
                  trigger='click'>
                  {Object.entries(SHOW_COMPLETED_TASK_FILTER_NAME).map(([key, label]) => (
                    <DropdownItem
                      key={key}
                      label={label}
                      onClick={() => handleShowCompletedTaskByFilterChange(Number(key))}
                      isSelected={displayFilters.showCompletedTaskByFilter === Number(key)}
                    />
                  ))}
                </Dropdown>
              </Data>
            )}
          </TaskDrop>
        )}
        <TaskDrop>
          <Data>
            <GroupDiv>
              <DropText>Show sub-tasks</DropText>
            </GroupDiv>
            <Switch>
              <label className='switch'>
                <input
                  type='checkbox'
                  checked={displayFilters.showSubTask}
                  onChange={(e) => handleShowSubTaskChange(e.target.checked)}
                />
                <span className='slider round'></span>
              </label>
            </Switch>
          </Data>
          {displayFilters.taskViewType !== TASK_LIST_TYPE.CALANDAR_VIEW && (
            <Data>
              <GroupDiv>
                <DropText>Show empty groups</DropText>
              </GroupDiv>
              <Switch>
                <label className='switch'>
                  <input
                    type='checkbox'
                    checked={displayFilters.showEmptyGroup}
                    onChange={(e) => handleShowEmptyGroupChange(e.target.checked)}
                  />
                  <span className='slider round'></span>
                </label>
              </Switch>
            </Data>
          )}
        </TaskDrop>
      </TaskFilterDiv>
    </TaskDropdown>
  );

  return (
    <TaskFilterCustomDropdown
      isMinWidth={289}
      trigger={
        <DisplayBtn
          onClick={(e) => {
            e.stopPropagation();
            taskStore.setIsTaskFilterOpen(!taskStore.isTaskFilterOpen);
          }}>
          <SVGIcon name='filter-icon' width='16' height='16' viewBox='0 0 18 18' />
          Display
        </DisplayBtn>
      }
      content={renderTaskFilterDropdown()}
      isTaskFilterOpen={isTaskFilterOpen}
    />
  );
};

export default observer(DisplayFilterDropdown);
